import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

/**
 * Custom hook to check if a property is in the user's wishlist
 * @param propertyId The ID of the property to check
 * @returns Object with isInWishlist status and loading state
 */
export const useIsInWishlist = (propertyId: number) => {
  const [isInWishlist, setIsInWishlist] = useState(false);

  // Get authentication status
  const { data: authData, isLoading: authLoading } = useQuery({
    queryKey: ['/api/auth/me'],
    retry: false,
    refetchOnWindowFocus: false,
  });

  const isAuthenticated = !!authData?.user;

  // Get wishlist items if authenticated
  const { data: wishlistData, isLoading: wishlistLoading } = useQuery({
    queryKey: ['/api/wishlist'],
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (wishlistData?.wishlist) {
      const found = wishlistData.wishlist.some((item: any) => 
        item.propertyId === propertyId || 
        (item.property && item.property.id === propertyId)
      );
      setIsInWishlist(found);
    }
  }, [wishlistData, propertyId]);

  return {
    isInWishlist,
    isLoading: authLoading || wishlistLoading
  };
};

/**
 * Utility function to toggle a property in the wishlist
 * @param propertyId The ID of the property to toggle
 * @param isInWishlist Current wishlist status of the property
 * @returns Promise that resolves when the API request is complete
 */
export const toggleWishlistItem = async (propertyId: number, isInWishlist: boolean) => {
  const method = isInWishlist ? 'DELETE' : 'POST';
  
  const response = await fetch(`/api/wishlist/${propertyId}`, {
    method,
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
  
  if (!response.ok) {
    throw new Error(await response.text());
  }
  
  return response.json();
};

/**
 * Counts total items in the wishlist
 * @param wishlistData The wishlist data from the API
 * @returns The count of items in the wishlist
 */
export const getWishlistCount = (wishlistData: any) => {
  return wishlistData?.wishlist?.length || 0;
};

/**
 * Groups wishlist items by property type
 * @param wishlistData The wishlist data from the API
 * @returns An object with counts by property type
 */
export const getWishlistByPropertyType = (wishlistData: any) => {
  if (!wishlistData?.wishlist?.length) return {};
  
  return wishlistData.wishlist.reduce((acc: Record<string, number>, item: any) => {
    const property = item.property;
    if (property) {
      const type = property.propertyType;
      acc[type] = (acc[type] || 0) + 1;
    }
    return acc;
  }, {});
};
